import React, { useState, useEffect } from 'react';
import { Modal } from '../../admin/Modal';

interface Coach {
  id: string;
  name: string;
  email: string;
}

interface CoachAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (coachId: string) => void;
  currentCoachId?: string;
  clientName: string;
  mode: 'assign' | 'change';
}

export const CoachAssignmentModal: React.FC<CoachAssignmentModalProps> = ({
  isOpen,
  onClose,
  onAssign,
  currentCoachId,
  clientName,
  mode
}) => {
  const [selectedCoachId, setSelectedCoachId] = useState(currentCoachId || '');
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadCoaches();
      if (currentCoachId) {
        setSelectedCoachId(currentCoachId);
      }
    }
  }, [isOpen, currentCoachId]);

  const loadCoaches = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/staff/list', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Filter for coaches only
        const coachList = data.staff
          .filter((s: any) => s.roles?.includes('coach'))
          .map((s: any) => ({
            id: s.id,
            name: `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.email,
            email: s.email
          }));
        setCoaches(coachList);

        // Pre-select current coach if available, otherwise pre-select current user
        if (!currentCoachId && coachList.length > 0) {
          // Get current user from token
          const meResponse = await fetch('/api/auth/me', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          if (meResponse.ok) {
            const meData = await meResponse.json();
            setSelectedCoachId(meData.user.id);
          }
        }
      }
    } catch (error) {
      console.error('Error loading coaches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = () => {
    if (selectedCoachId) {
      onAssign(selectedCoachId);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'assign' ? 'Assign Coach' : 'Change Coach Assignment'}
      variant="info"
      size="small"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleAssign}
            disabled={!selectedCoachId || loading}
          >
            {mode === 'assign' ? 'Assign' : 'Reassign'}
          </button>
        </>
      }
    >
      <div style={{ marginBottom: '16px' }}>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '16px' }}>
          {mode === 'assign'
            ? `Select a coach to assign to ${clientName}`
            : `Select a new coach for ${clientName}`}
        </p>

        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
          {mode === 'assign' ? 'Assign to:' : 'Reassign to:'}
        </label>

        <select
          value={selectedCoachId}
          onChange={(e) => setSelectedCoachId(e.target.value)}
          disabled={loading}
          style={{
            width: '100%',
            padding: '10px 12px',
            fontSize: '14px',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            backgroundColor: 'var(--bg-primary)',
            color: 'var(--text-primary)',
            cursor: loading ? 'wait' : 'pointer'
          }}
        >
          {loading ? (
            <option value="">Loading coaches...</option>
          ) : (
            <>
              <option value="">-- Select a coach --</option>
              {coaches.map(coach => (
                <option key={coach.id} value={coach.id}>
                  {coach.name} ({coach.email})
                </option>
              ))}
            </>
          )}
        </select>
      </div>
    </Modal>
  );
};
